import { GenericValue } from 'hisng';

import {
    CALCULATED_FUNDS_HISKEYID, GRANTED_FUNDS_HISKEYID, REQUESTED_FUNDS_HISKEYID
} from 'app/fs/res/finance/model/constants';
import { ResearchContract } from 'app/fs/res/common/model/finance/research-contract-classes';

export class FinanceUtils {

    /* get sum granted amount of given funding programs */
    static getGrantedAmountSum( fundingPrograms: ResearchContract[] ): number {
        return this.getFinancingFundsTotal( fundingPrograms, GRANTED_FUNDS_HISKEYID );
    }

    /* get sum requested amount of funding programs */
    static getRequestedAmountSum( fundingPrograms: ResearchContract[] ): number {
        return this.getFinancingFundsTotal( fundingPrograms, REQUESTED_FUNDS_HISKEYID );
    }

    /* get sum calculated amount of funding programs */
    static getCalculatedAmountSum( fundingPrograms: ResearchContract[] ): number {
        return this.getFinancingFundsTotal( fundingPrograms, CALCULATED_FUNDS_HISKEYID );
    }

    /* get sum incomes amount of funding programs */
    static getIncomeAmount( fundingPrograms: ResearchContract[] ): number {
        let incomeAmount = 0;
        for ( let fundingProgram of fundingPrograms ) {
            if ( fundingProgram.incomes ) {
                for ( let income of fundingProgram.incomes ) {
                    if ( income.incomeAmount ) {
                        if ( !isNaN( income.incomeAmount as number ) ) {
                            incomeAmount = incomeAmount + ( income.incomeAmount as number );
                        }
                    }
                }
            }
        }
        return incomeAmount;
    }

    /* get sum still available of funding programs*/
    static getStillAvailable( fundingPrograms: ResearchContract[] ): number {
        let stillAvailable = 0;
        let sumIncome = 0;
        let grantedSum = this.getFinancingFundsTotal( fundingPrograms, GRANTED_FUNDS_HISKEYID );
        if ( grantedSum ) {
            if ( this.getIncomeAmount( fundingPrograms ) !== 0 ) {
                sumIncome = this.getIncomeAmount( fundingPrograms );
            }
            stillAvailable = grantedSum - sumIncome;
        }
        return stillAvailable <= 0 ? 0 : stillAvailable;
    }

    /* get main funder of funding programs */
    static getMainFunder( fundingPrograms: ResearchContract[] ): GenericValue {
        let mainFunderMap = new Map<GenericValue, number>();
        let maxKsdfCategory: GenericValue = null;
        let maxValue = -1;

        fundingPrograms.forEach( f => {
            if ( f.budgetSource && f.budgetSource.kdsfCategory ) {
                let grantedSum = this.getFinancingFundsTotal( new Array<ResearchContract>( f ), GRANTED_FUNDS_HISKEYID );
                let key: GenericValue = f.budgetSource.kdsfCategory;
                if ( mainFunderMap.has( key ) ) {
                    mainFunderMap.set( key, ( mainFunderMap.get( key ) + ( grantedSum as number ) ) );
                } else {
                    if ( grantedSum ) {
                        mainFunderMap.set( key, ( grantedSum as number ) );
                    } else {
                        mainFunderMap.set( key, 0 );
                    }
                }
            }
        } );

        // get highest value from map
        mainFunderMap.forEach( ( value, key ) => {
            if ( value >= maxValue ) {
                if ( value === maxValue ) {
                    maxKsdfCategory = null;
                } else {
                    maxValue = value;
                    maxKsdfCategory = key;
                }
            }
        } );
        return maxKsdfCategory;
    }

    /* get research funds total */
    static getFinancingFundsTotal( researchContracts: ResearchContract[], researchFundTypeHiskeyId: number ): number {
        let total = 0;
        researchContracts.forEach( rC => {
            if( ( rC.requestFunds != null && rC.requestFunds.length !== 0 ) ) {
                let filteredResearchFunds = rC.requestFunds.filter( rF => rF.researchFundType.hiskeyId === researchFundTypeHiskeyId );
                filteredResearchFunds.forEach( fF => {
                    if ( !isNaN( fF.incomeAmount as number ) && fF.budgetPosition !== null ) {
                        total += ( fF.incomeAmount as number );
                    }
                } );
            }
        } );
        return total;
    }

    /* get highest research fund type value of research contract funds */
    static getHighestResearchFundTypeHiskeyId( researchContract: ResearchContract ): number {
        let typedResearchFunds = researchContract.requestFunds.filter(
            rF => ( rF.researchFundType.hiskeyId === GRANTED_FUNDS_HISKEYID && rF.incomeAmount ) );
        if ( typedResearchFunds.length !== 0 ) {
            return GRANTED_FUNDS_HISKEYID;
        }
        typedResearchFunds = researchContract.requestFunds.filter(
            rF => ( rF.researchFundType.hiskeyId === REQUESTED_FUNDS_HISKEYID && rF.incomeAmount ) );
        if ( typedResearchFunds.length !== 0 ) {
            return REQUESTED_FUNDS_HISKEYID;
        }
        typedResearchFunds = researchContract.requestFunds.filter(
            rF => ( rF.researchFundType.hiskeyId === CALCULATED_FUNDS_HISKEYID && rF.incomeAmount ) );
        if ( typedResearchFunds.length !== 0 ) {
            return CALCULATED_FUNDS_HISKEYID;
        }
        return undefined;
    }

    /* if research contract is defined dummy object */
    static isDummyResearchContract( rc: ResearchContract ): boolean {
        return !rc.budgetSource;
    }

    /* filter dummy budget source from given research contracts array */
    static getResearchContractsWithoutDummies( researchContracts: ResearchContract[] ): ResearchContract[] {
        return researchContracts.filter( rc => rc.budgetSource );
    }
}
